import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { FinalCTA } from '../components/FinalCTA';
import { Target, Heart, Zap, Users } from 'lucide-react';
export function AboutPage() {
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen bg-white font-sans text-gray-900 selection:bg-blue-600 selection:text-white" data-id="element-548">
      <Header data-id="element-549" />

      {/* Hero Section */}
      <section className="relative pt-24 pb-12 sm:pt-32 sm:pb-20 lg:pt-48 lg:pb-32 overflow-hidden" data-id="element-550">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-100 via-gray-50 to-white opacity-70" data-id="element-551"></div>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center" data-id="element-552">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-553">
            <span className="mb-4 inline-block rounded-full bg-blue-100 px-4 py-1.5 text-sm font-bold text-blue-600" data-id="element-554">
              NUESTRA HISTORIA
            </span>
            <h1 className="mb-6 text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl md:text-6xl lg:text-7xl" data-id="element-555">
              Construyendo confianza <br data-id="element-556" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600" data-id="element-557">
                desde 1985.
              </span>
            </h1>
            <p className="mx-auto max-w-2xl text-lg sm:text-xl text-gray-600 leading-relaxed" data-id="element-558">
              Somos más que una constructora. Somos tu socio estratégico que
              transforma espacios vacíos en oportunidades de negocio.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-white" data-id="element-559">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-560">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center" data-id="element-561">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="relative aspect-[4/5] rounded-[2rem] sm:rounded-[2.5rem] overflow-hidden shadow-2xl shadow-blue-900/10" data-id="element-562">
              <img src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&q=80&w=1000" alt="Arquitectura moderna" className="object-cover w-full h-full" data-id="element-563" />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 to-transparent" data-id="element-564"></div>
              <div className="absolute bottom-6 left-6 sm:bottom-10 sm:left-10 text-white" data-id="element-565">
                <p className="text-3xl sm:text-4xl font-bold" data-id="element-566">1984</p>
                <p className="text-base sm:text-lg opacity-90" data-id="element-567">
                  Año de fundación
                </p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="space-y-6 sm:space-y-8" data-id="element-568">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900" data-id="element-569">
                Evolución constante
              </h2>
              <div className="space-y-4 sm:space-y-6 text-base sm:text-lg text-gray-600 leading-relaxed" data-id="element-570">
                <p data-id="element-571">
                  Forax nació con una visión clara: elevar el estándar de la
                  construcción corporativa en Chile. Lo que comenzó como una
                  pequeña empresa familiar se ha transformado en un referente
                  del sector, manteniendo siempre la esencia de cercanía y
                  compromiso.
                </p>
                <p data-id="element-572">
                  A lo largo de cuatro décadas, hemos sido testigos y partícipes
                  de la evolución de los espacios de trabajo y retail. Hemos
                  pasado de las oficinas cerradas a los espacios colaborativos,
                  de las tiendas tradicionales a las experiencias de marca
                  inmersivas.
                </p>
                <p data-id="element-573">
                  Hoy, combinamos nuestra sólida experiencia técnica con
                  metodologías ágiles y tecnología de punta, ofreciéndote la
                  seguridad de una empresa consolidada con la frescura de una
                  startup.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 sm:py-20 md:py-24 bg-gray-50" data-id="element-574">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-575">
          <div className="text-center mb-10 sm:mb-16" data-id="element-576">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl" data-id="element-577">
              Nuestros Pilares
            </h2>
            <p className="mt-4 text-lg sm:text-xl text-gray-500" data-id="element-578">
              Los valores que guían cada decisión.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8" data-id="element-579">
            {[{
            icon: Target,
            title: 'Excelencia',
            desc: 'No negociamos la calidad. Cada detalle cuenta.'
          }, {
            icon: Heart,
            title: 'Compromiso',
            desc: 'Tu proyecto es nuestro proyecto. Nos involucramos al 100%.'
          }, {
            icon: Zap,
            title: 'Agilidad',
            desc: 'Respuestas rápidas y soluciones eficientes a imprevistos.'
          }, {
            icon: Users,
            title: 'Cercanía',
            desc: 'Trato directo, transparente y humano en cada etapa.'
          }].map((value, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-white p-6 sm:p-8 rounded-2xl sm:rounded-3xl shadow-lg hover:shadow-xl transition-shadow" data-id="element-580">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-blue-100 rounded-2xl flex items-center justify-center text-blue-600 mb-4 sm:mb-6" data-id="element-581">
                  <value.icon className="w-6 h-6 sm:w-7 sm:h-7" data-id="element-582" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3" data-id="element-583">
                  {value.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-500" data-id="element-584">
                  {value.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      <FinalCTA data-id="element-585" />
      <Footer data-id="element-586" />
    </div>;
}